import { api, ApiError } from '../../core/network/api';
import { esc } from './item-icon';
import { mountBackToLanding } from './back-to-landing';
import { getIntlLocale, t } from '../i18n/i18n';

/**
 * `/legal/<slug>` — one CMS legal page (2026-10-13), the target of the links
 * in the landing footer. Public: read signed out, from core's
 * `GET /api/cms/legal/:slug` (active pages only; 404 otherwise).
 *
 * The admin edits these in a plain textarea, so content is treated as text:
 * escaped, a blank line starts a paragraph, a single newline is a line break.
 * Nothing an admin types is ever interpreted as markup.
 */
export interface LegalPageData {
  slug: string;
  title: string;
  content: string;
  updatedAt?: string;
}

export function slugFromPath(pathname: string): string | null {
  const m = /^\/legal\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

export function legalContentHtml(content: string): string {
  return content
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p class="mb-4 last:mb-0">${esc(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** `bg-auth-gradient` brings the backdrop, the logo and the room above it for the logo. */
function shell(inner: string): string {
  return `
    <main class="bg-auth-gradient px-4 pb-12">
      <article id="legal-page" class="mx-auto w-full max-w-[760px] bg-auth-card border border-border rounded-lg p-6 sm:p-10 shadow-lift">
        ${inner}
      </article>
    </main>`;
}

export function mountLegalPage(root: HTMLElement, slug: string): () => void {
  let alive = true;
  root.innerHTML = shell(`<p class="text-text-dim text-sm">${esc(t('Loading…'))}</p>`);
  const unmountBack = mountBackToLanding(root);

  void (async () => {
    let html: string;
    try {
      const page = await api.get<LegalPageData>(`/cms/legal/${encodeURIComponent(slug)}`);
      document.title = `${page.title} — Tallow`;
      const updated = page.updatedAt ? new Date(page.updatedAt) : null;
      html = `
        <h1 class="font-heading text-gold text-2xl sm:text-3xl tracking-wide mb-2">${esc(page.title)}</h1>
        ${updated && !Number.isNaN(updated.getTime())
          ? `<p class="text-text-dim text-xs mb-6">${esc(t('Last updated {date}', { date: updated.toLocaleDateString(getIntlLocale()) }))}</p>`
          : '<div class="mb-6"></div>'}
        <div class="text-text text-[15px] leading-relaxed break-words">${legalContentHtml(page.content)}</div>`;
    } catch (err) {
      const missing = err instanceof ApiError && err.status === 404;
      html = `
        <h1 class="font-heading text-gold text-2xl mb-3">${esc(missing ? t('Page not found') : t('Could not load this page'))}</h1>
        <p class="text-text-dim text-sm">${esc(missing
          ? t('This page does not exist or is no longer published.')
          : t('Please try again in a moment.'))}</p>`;
    }
    if (!alive) return;
    const article = root.querySelector('#legal-page');
    if (article) article.innerHTML = html;
  })();

  return () => {
    alive = false;
    unmountBack();
  };
}
